import { BillingUsers } from "@mcleod/ar/src/BillingUsers";
import { Button, ClickEvent, DataSource, DataSourceMode, Layout, SaveButton, SlideoutDecorator, Table } from "@mcleod/components";
import { ModelRow } from "@mcleod/core";
import { MovementUsers } from "@mcleod/dispatch/src/MovementUsers";
import { ResponsibilityUtil } from "./ResponsibilityUtil";
import { ResponsibleHist } from "./ResponsibleHist";

/**
 * The purpose of this class is to display the provided layout in a SlideoutDecorator.  The provided layout is expected
 * to be a 'users info' layout from a transaction screen.  For example, this would be the OrderUsers layout from the Orders
 * screen.  The important thing about the provided layout is that it is expected to contain an instance of
 * ResponsibleHist as a nested layout.
 */
export class UsersResponsibilitySlideout {
    public layout: Layout;
    private layoutName: string;
    private title: string;
    private idFilter: any;
    private responsibleHist: ResponsibleHist;
    private layoutRespsonsibleHistDataSource: DataSource;
    private parentMainDataSource: DataSource;
    private parentRespsonsibleHistDataSource: DataSource;
    private isCommissionsEnabled: boolean;
    private enableCustomerCommissions: boolean;
    private commissionRoleType: string;
    public includeCommissions: boolean = false;
    private isBillingCreditRecord: boolean = false;

    public constructor(cfg: UsersResponsibilitySlideoutConfig) {
        this.layoutName = cfg.layoutName;
        this.title = cfg.title;
        this.idFilter = cfg.idFilter;
        this.parentMainDataSource = cfg.parentMainDataSource;
        this.parentRespsonsibleHistDataSource = cfg.parentRespsonsibleHistDataSource;
        this.includeCommissions = cfg.includeCommissions ?? false;
        this.isCommissionsEnabled = cfg.enableCommissions;
        this.enableCustomerCommissions = cfg.enableCustomerCommissions;
        this.isBillingCreditRecord = cfg.isBillingCreditRecord ?? false;
        this.layout = Layout.getLayout(this.layoutName);
        if (this.isBillingCreditRecord && this.layout instanceof BillingUsers) {
            this.layout.isCreditBill = this.isBillingCreditRecord;
        }
        this.setupLayoutLoadListener();
    }

    public static builder(): UsersResponsibilitySlideoutBuilder {
        return new UsersResponsibilitySlideoutBuilder();
    }

    setupLayoutLoadListener() {
        this.layout.addLayoutLoadListener(async event => {
            this.responsibleHist = this.findResponsibleHistNestedLayout(this.layout);
            if (this.responsibleHist != null) {
                this.layoutRespsonsibleHistDataSource = this.findResponsibleHistDataSource(this.layout);
                await this.responsibleHist.setup(this.layoutRespsonsibleHistDataSource);
            }
            const isManifested = this.parentMainDataSource.activeRow.get("manifest_id", null) !== null;
                if (!isManifested || !this.isCommissionsEnabled || this.layoutName != "lme/dispatch/MovementUsers") {
                    this.responsibleHist.tableResponsibleHist.removeColumn("Order ID");
                }

                if (!(this.isCommissionsEnabled && (this.enableCustomerCommissions || this.includeCommissions))) {
                    this.responsibleHist.tableResponsibleHist.removeColumn("Commissions Role Type");
                    this.responsibleHist.tableResponsibleHist.removeColumn("Commissions Eligible");
                    this.responsibleHist.tableResponsibleHist.removeColumn("Split %");
                }
        });
    }

    public show() {
        const mode = this.parentMainDataSource.mode;
        const slideoutButton: Button | SaveButton = ResponsibilityUtil.getSlideoutButton(mode);
        if (slideoutButton != null && slideoutButton instanceof Button) {
            slideoutButton.addClickListener((event: ClickEvent) => {
                this.processSearchClick(event.target as Button, sod)
            });
        }
        const sod = new SlideoutDecorator({
            layout: this.layout,
            width: "65%",
            maxWidth: 1320,
            fillVerticalSpace: true,
            title: this.title,
            displayOverlay: true,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: [slideoutButton],
            onClose: () => {
                sod.slideOut();
            },
            layoutLoadListeners: () => {
                slideoutButton.dataSource = sod.layout.mainDataSource;
                if (this.idFilter != null) {
                    this.layout.mainDataSource.search({ id : this.idFilter }).then(response => {
                    this.layout.mainDataSource.setRowsAndMode(DataSourceMode.UPDATE, response?.modelRows as ModelRow[]);});
                }
                const movementUsers = this.layout as MovementUsers;
                const tableResponsibleHist: Table = movementUsers.layoutResponsibleHist.tableResponsibleHist;
                slideoutButton.visible = tableResponsibleHist.allowDelete || tableResponsibleHist.allowEdit;
            },
            doAfterSlideIn: (sod: SlideoutDecorator) => {
                this.loadData();
            }
        });
    }

    private async processSearchClick(button: Button, sod: SlideoutDecorator) {
        try {
            button.busy = true;
            const mode = this.parentMainDataSource.mode;
            if (mode === DataSourceMode.SEARCH) {
                if (this.responsibleHist != null) {
                    this.parentRespsonsibleHistDataSource.data = this.layoutRespsonsibleHistDataSource.data;
                }
                sod.slideOut();
            }
        } finally {
            button.busy = false;
        }
    }

    public loadData() {
        if (this.parentMainDataSource.mode === DataSourceMode.UPDATE) {
            if (this.parentMainDataSource.url == this.layout.mainDataSource.url) {
                this.layout.mainDataSource.setRowsAndMode(this.parentMainDataSource.mode, this.parentMainDataSource.data);
            } else {
                this.layout.mainDataSource.search({ ...this.idFilter });
            }
        } else if (this.parentMainDataSource.mode === DataSourceMode.ADD) {
            if (this.layoutRespsonsibleHistDataSource != null) {
                this.layoutRespsonsibleHistDataSource.data = this.parentRespsonsibleHistDataSource.getDataCopy();
                this.layoutRespsonsibleHistDataSource.displayDataInBoundComponents();
                if (this.parentMainDataSource.activeRow.get("rf_entry_code_required", false) === true)
                    this.responsibleHist.addBlankEntryRecordToTable();
            }
        }
        this.layout.mainDataSource.mode = this.parentMainDataSource.mode;
        if (this.layoutRespsonsibleHistDataSource != null)
            this.layoutRespsonsibleHistDataSource.mode = this.parentMainDataSource.mode;

        this.responsibleHist.commissionRoleType = this.commissionRoleType;
    }

    private findResponsibleHistNestedLayout(layout: Layout): ResponsibleHist {
        for (const variable of Object.keys(layout)) {
            if (layout[variable] instanceof ResponsibleHist)
                return layout[variable];
        }
        return null; //can return null if AgenciesWithPrimary nested layout was removed due to licensing
    }

    private findResponsibleHistDataSource(layout: Layout): DataSource {
        for (const dataSourceId of layout.dataSourceIDs) {
            if ((layout[dataSourceId] as DataSource).url === "lme/general/responsible-hist")
                return layout[dataSourceId];
        }
        throw new Error("The provided layout does not contain the expected Responsibility Codes DataSource.");
    }

}

// Builder config interface
interface UsersResponsibilitySlideoutConfig {
    layoutName: string;
    title: string;
    idFilter?: string;
    parentMainDataSource: DataSource;
    parentRespsonsibleHistDataSource: DataSource;
    isBillingCreditRecord?: boolean;
    includeCommissions?: boolean;
    enableCommissions: boolean;
    enableCustomerCommissions: boolean;
    commissionRoleType?: string;
}
export class UsersResponsibilitySlideoutBuilder {
    private cfg: Partial<UsersResponsibilitySlideoutConfig> = {};

    layoutName(value: string): this { this.cfg.layoutName = value; return this; }
    title(value: string): this { this.cfg.title = value; return this; }
    idFilter(value: any): this { this.cfg.idFilter = value; return this; }
    parentMainDataSource(ds: DataSource): this { this.cfg.parentMainDataSource = ds; return this; }
    parentRespsonsibleHistDataSource(ds: DataSource): this { this.cfg.parentRespsonsibleHistDataSource = ds; return this; }
    includeCommissions(flag: boolean): this { this.cfg.includeCommissions = flag; return this; }
    enableCommissions(flag: boolean): this { this.cfg.enableCommissions = flag; return this; }
    enableCustomerCommissions(flag: boolean): this { this.cfg.enableCustomerCommissions = flag; return this; }
    isBillingCreditRecord(flag: boolean): this { this.cfg.isBillingCreditRecord = flag; return this; }
    commissionRoleType(value: string): this { this.cfg.commissionRoleType = value; return this; }

    build(): UsersResponsibilitySlideout {
        const missing: string[] = [];
        if (!this.cfg.layoutName) missing.push("layoutName");
        if (!this.cfg.title) missing.push("title");
        if (!this.cfg.parentMainDataSource) missing.push("parentMainDataSource");
        if (!this.cfg.parentRespsonsibleHistDataSource) missing.push("parentRespsonsibleHistDataSource");
        if (missing.length) {
            throw new Error("UsersResponsibilitySlideoutBuilder missing required: " + missing.join(", "));
        }
        return new UsersResponsibilitySlideout(this.cfg as UsersResponsibilitySlideoutConfig);
    }
}
